import useFormValidation from "hooks/useFormValidation";
import { Label } from "layout/typography/Label";
import { sortBy } from "lodash";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { useState } from "react";
import { Controller, useFormContext, useFieldArray } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useGetPayersByAccountQuery } from "store/queries/payers";

import { specialtyDropdown } from "../../../helpers/providerConstants";
import { formValidationConstants } from "../helpers/formValidations";

type PayerFeeType = "ClearPatientFee" | "GfeCreditCardFee";

const PAYER_FEE_SECTIONS: {
  type: PayerFeeType;
  title: string;
  valueLabel: string;
  help?: string;
}[] = [
  {
    type: "ClearPatientFee",
    title: "Clear Patient Fee",
    valueLabel: "Clear Patient Fee",
  },
  {
    type: "GfeCreditCardFee",
    title: "Surcharge Fee",
    valueLabel: "Surcharge fee %",
    help: "Charged to the patient on top only when they pay with a CREDIT card (Finix); the same amount comes off the Clear fee for that charge. Debit cards: nothing changes. Works only together with the Clear fee for GFE — a payer has either Clear fee + Surcharge, or Clear fee alone, or Clear Patient Fee alone.",
  },
];

export function ClearFee() {
  const [expanded, setExpanded] = useState(false);
  const {
    control,
    trigger,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<any>();

  const { id: accountId } = useParams<{ id: string }>();

  // We only fetch payers if we have an accountId which means we are in edit page, not int he create page
  const { data: payerByAccount, isLoading: loadingPayersName } =
    useGetPayersByAccountQuery(accountId || "", {
      skip: !accountId,
    });

  // Generate payer options from the fetched data
  const payerNameOptions = sortBy(
    payerByAccount?.data?.map((item: any) => ({
      name: item.name,
      value: { name: item.name, payerId: item.id },
    })) || [],
    ["name"]
  );

  // One field array holds every per-payer fee row; each section below renders only the rows of
  // its own fee type (`ClearPatientFee`, and since CLE-2798 `GfeCreditCardFee`).
  const { fields, append, remove } = useFieldArray({
    control,
    name: "payerAccountFees",
  });

  const { getFormErrorMessage, handleOnBlurField } = useFormValidation();

  // Only show the per-payer fee sections if we have an accountId and payers are available
  const showClearPatientFee =
    accountId && payerNameOptions.length > 0 && !loadingPayersName;

  // We need to watch all payer selections to check for duplicates
  const watchedPayerFees = watch("payerAccountFees") || [];

  // Payers already carrying a row of the OTHER type are not offered in a section's dropdown.
  const payerOptionsFor = (feeType: PayerFeeType, currentPayerId?: string) =>
    payerNameOptions.filter((payer) => {
      if (payer.value.payerId === currentPayerId) return true;
      return !watchedPayerFees.some(
        (fee: any) =>
          fee.payerId === payer.value.payerId &&
          (fee.type || "ClearPatientFee") !== feeType
      );
    });

  // A payer carries either a Clear Patient Fee or a Surcharge fee. When the other section already
  // holds every payer, "Add Payer Fee" / "Apply to all payers" would silently do nothing — say why.
  const [sectionNotice, setSectionNotice] = useState<
    Record<PayerFeeType, string>
  >({ ClearPatientFee: "", GfeCreditCardFee: "" });
  const otherSectionTitle = (feeType: PayerFeeType) =>
    PAYER_FEE_SECTIONS.find((s) => s.type !== feeType)?.title ?? "other";
  const noticeFor = (feeType: PayerFeeType, text: string) =>
    setSectionNotice((prev) => ({ ...prev, [feeType]: text }));

  const addPayerFee = (feeType: PayerFeeType = "ClearPatientFee") => {
    if (payerOptionsFor(feeType).length === 0) {
      noticeFor(
        feeType,
        `Every payer already has a ${otherSectionTitle(
          feeType
        )}. A payer can have either a Clear Patient Fee or a Surcharge fee — remove it from the other section first.`
      );
      return;
    }
    noticeFor(feeType, "");
    append({
      payerId: "",
      payerName: "",
      type: feeType,
      valueInPercentage: 0,
    });
  };

  // CLE-2798 — Ani's "easy option to select all payers": one percent, one click, a row for every
  // payer of this fee type (existing rows of the type are updated, missing ones appended).
  const [allPayersPercent, setAllPayersPercent] = useState<
    Record<PayerFeeType, string>
  >({ ClearPatientFee: "", GfeCreditCardFee: "" });
  // When every eligible payer carries the same rate the section shows ONE "All payers" line instead
  // of a row per payer (20 payers, 20 identical rows otherwise). "Edit per payer" expands the rows.
  const [perPayerView, setPerPayerView] = useState<
    Record<PayerFeeType, boolean>
  >({ ClearPatientFee: false, GfeCreditCardFee: false });

  const applyToAllPayers = (feeType: PayerFeeType) => {
    const percent = parseFloat(allPayersPercent[feeType]);
    if (!(percent > 0)) return;
    const eligible = payerOptionsFor(feeType);
    const skipped = payerNameOptions.length - eligible.length;
    if (eligible.length === 0) {
      noticeFor(
        feeType,
        `Every payer already has a ${otherSectionTitle(
          feeType
        )}. A payer can have either a Clear Patient Fee or a Surcharge fee — remove it from the other section first.`
      );
      return;
    }
    noticeFor(
      feeType,
      skipped > 0
        ? `Applied to ${eligible.length} payer${
            eligible.length === 1 ? "" : "s"
          }; ${skipped} skipped because ${
            skipped === 1 ? "it already has" : "they already have"
          } a ${otherSectionTitle(feeType)}.`
        : ""
    );
    const rows: any[] = watchedPayerFees;
    rows.forEach((fee: any, index: number) => {
      if (fee.type === feeType) {
        setValue(`payerAccountFees.${index}.valueInPercentage`, percent, {
          shouldDirty: true,
        });
      }
    });
    const coveredPayerIds = new Set(rows.map((f: any) => f.payerId));
    payerOptionsFor(feeType)
      .filter((payer) => !coveredPayerIds.has(payer.value.payerId))
      .forEach((payer) => {
        append({
          payerId: payer.value.payerId,
          payerName: payer.value.name,
          type: feeType,
          valueInPercentage: percent,
        });
      });
  };

  // A payer appears ONCE across both sections: it has either a Clear Patient Fee or a Surcharge
  // fee, never both (CLE-2798 — the surcharge only works together with the Clear fee).
  const isPayerAlreadySelected = (currentIndex: number, payerId: string) => {
    if (!payerId) return false;
    return watchedPayerFees.some(
      (fee: any, index: number) =>
        index !== currentIndex && fee.payerId === payerId
    );
  };

  // The surcharge comes off the Clear fee for GFE, so without that fee there is nothing for it
  // to work with — the section is disabled until a Clear fee for GFE > 0 is entered.
  const gfeClearFeeField = watch("clearHealthFeeForGFE");
  const surchargeDisabled = !(parseFloat(gfeClearFeeField) > 0);

  return (
    <div className="block bg-white border-round-xl p-4 border-1 border-gray-300">
      <button
        type="button"
        className="flex align-items-center justify-content-between w-full border-none bg-transparent p-0 cursor-pointer text-left"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <span className="text-black font-bold text-lg m-0">
          Clear Fee Section
        </span>
        <i
          className={`pi pi-chevron-down text-gray-600 transition-duration-200 transition-all ${
            expanded ? "rotate-180" : ""
          }`}
          style={{ fontSize: "0.75rem" }}
          aria-hidden
        />
      </button>
      {expanded && (
        <div className="flex flex-column gap-4">
          <div className="formgrid grid mt-4 border-bottom-1 pb-2 border-gray-300">
            <div className="field col-12 md:col-4">
              <Label htmlFor="clearFeeForClearPackage">
                Fee for Clear package
              </Label>
              <Controller
                name="clearFeeForClearPackage"
                control={control}
                rules={{
                  required:
                    formValidationConstants.CLEAR_FEE
                      .CLEAR_FEE_FOR_CLEAR_PACKAGE,
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <InputText
                      className="w-full"
                      id="clearFeeForClearPackage"
                      autoComplete="off"
                      type="number"
                      value={value || ""}
                      placeholder="Enter amount here"
                      aria-labelledby="clearFeeForClearPackage"
                      min={0}
                      onBlur={() => {
                        handleOnBlurField({
                          onBlur,
                          field: "clearFeeForClearPackage",
                          trigger,
                        });
                      }}
                      onChange={onChange}
                    />
                    {getFormErrorMessage("clearFeeForClearPackage", errors)}
                  </>
                )}
              />
            </div>
            <div className="field col-12 md:col-4">
              <Label htmlFor="clearTransactionFeeForPR">
                Fee for Patient Balance
              </Label>
              <Controller
                name="clearTransactionFeeForPR"
                control={control}
                rules={{
                  required:
                    formValidationConstants.CLEAR_FEE
                      .CLEAR_TRANSACTION_FEE_FOR_PR,
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <InputText
                      className="w-full"
                      id="clearTransactionFeeForPR"
                      autoComplete="off"
                      value={value || ""}
                      placeholder="Enter amount here"
                      aria-labelledby="clearTransactionFeeForPR"
                      type="number"
                      min={0}
                      onBlur={() => {
                        handleOnBlurField({
                          onBlur,
                          field: "clearTransactionFeeForPR",
                          trigger,
                        });
                      }}
                      onChange={onChange}
                    />
                    {getFormErrorMessage("clearTransactionFeeForPR", errors)}
                  </>
                )}
              />
            </div>
            <div className="field col-12 md:col-4">
              <Label htmlFor="clearHealthFeeForGFE">
                Fee for Patient Estimate
              </Label>
              <Controller
                name="clearHealthFeeForGFE"
                control={control}
                rules={{
                  required:
                    formValidationConstants.CLEAR_FEE.CLEAR_HEALTH_FEE_FOR_GFE,
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <InputText
                      className="w-full"
                      id="clearHealthFeeForGFE"
                      autoComplete="off"
                      value={value || ""}
                      placeholder="Enter amount here"
                      aria-labelledby="clearHealthFeeForGFE"
                      type="number"
                      min={0}
                      onBlur={() => {
                        handleOnBlurField({
                          onBlur,
                          field: "clearHealthFeeForGFE",
                          trigger,
                        });
                      }}
                      onChange={onChange}
                    />
                    {getFormErrorMessage("clearHealthFeeForGFE", errors)}
                  </>
                )}
              />
            </div>
            {/* <div className="field col-12 md:col-4">
              <Label htmlFor="clearFinancingFee">Financing Fee</Label>
              <Controller
                name="clearFinancingFee"
                control={control}
                rules={{
                  required:
                    formValidationConstants.CLEAR_FEE.CLEAR_FINANCING_FEE,
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <InputText
                      className="w-full"
                      id="clearFinancingFee"
                      autoComplete="off"
                      value={value || ""}
                      placeholder="Enter amount here"
                      aria-labelledby="clearFinancingFee"
                      type="number"
                      min={0}
                      onBlur={() => {
                        handleOnBlurField({
                          onBlur,
                          field: "clearFinancingFee",
                          trigger,
                        });
                      }}
                      onChange={onChange}
                    />
                    {getFormErrorMessage("clearFinancingFee", errors)}
                  </>
                )}
              />
            </div> */}
            <div className="field col-12 md:col-4">
              <Label htmlFor="clearHealthDiscountForGFE">
                Patient Estimate Discount
              </Label>
              <Controller
                name="clearHealthDiscountForGFE"
                control={control}
                rules={{
                  required:
                    formValidationConstants.CLEAR_FEE
                      .CLEAR_HEALTH_DISCOUNT_FOR_GFE,
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <InputText
                      className="w-full"
                      id="clearHealthDiscountForGFE"
                      autoComplete="off"
                      value={value ?? ""}
                      placeholder="Enter amount here"
                      aria-labelledby="clearHealthDiscountForGFE"
                      type="number"
                      min={0}
                      onBlur={() => {
                        handleOnBlurField({
                          onBlur,
                          field: "clearHealthDiscountForGFE",
                          trigger,
                        });
                      }}
                      onChange={onChange}
                    />
                    {getFormErrorMessage("clearHealthDiscountForGFE", errors)}
                  </>
                )}
              />
            </div>
            {/* <div className="field col-12 md:col-4">
              <Label htmlFor="prFeeMethod">
                Patient Responsibility Fee method
              </Label>
              <Controller
                data-testid="prFeeMethod"
                name="prFeeMethod"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Dropdown
                    id="prFeeMethod"
                    data-testid="prFeeMethod__Dropdown"
                    value={value}
                    onChange={onChange}
                    options={Object.values(specialtyDropdown)}
                    optionLabel="name"
                    placeholder="Select"
                    className="w-full"
                    disabled
                  />
                )}
              />
            </div> */}
            <div className="field col-12 md:col-4">
              <Label htmlFor="patientResponsibilityDiscount">
                Patient Balance discount
              </Label>
              <Controller
                name="patientResponsibilityDiscount"
                control={control}
                rules={{
                  required:
                    formValidationConstants.CLEAR_FEE
                      .PATIENT_RESPONSIBILITY_DISCOUNT,
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <InputText
                      className="w-full"
                      id="patientResponsibilityDiscount"
                      autoComplete="off"
                      value={value ?? ""}
                      placeholder="Enter amount here"
                      aria-labelledby="patientResponsibilityDiscount"
                      type="number"
                      min={0}
                      onBlur={() => {
                        handleOnBlurField({
                          onBlur,
                          field: "patientResponsibilityDiscount",
                          trigger,
                        });
                      }}
                      onChange={onChange}
                    />
                    {getFormErrorMessage(
                      "patientResponsibilityDiscount",
                      errors
                    )}
                  </>
                )}
              />
            </div>
            <div className="field col-12 md:col-4">
              <Label htmlFor="expirationDays">Expiration Number of Days</Label>
              <Controller
                name="expirationDays"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <InputText
                    className="w-full"
                    id="expirationDays"
                    autoComplete="off"
                    value={value}
                    placeholder="Enter expiration number of days"
                    aria-labelledby="expirationDays"
                    type="number"
                    min={0}
                    onBlur={() => {
                      handleOnBlurField({
                        onBlur,
                        field: "expirationDays",
                        trigger,
                      });
                    }}
                    onChange={onChange}
                  />
                )}
              />
            </div>
          </div>
          {showClearPatientFee &&
            PAYER_FEE_SECTIONS.map((section) => {
              const sectionRows = fields
                .map((field, index) => ({ field, index }))
                .filter(
                  ({ index }) =>
                    (watchedPayerFees[index]?.type || "ClearPatientFee") ===
                    section.type
                );
              const sectionDisabled =
                section.type === "GfeCreditCardFee" && surchargeDisabled;
              const eligiblePayerIds = payerOptionsFor(section.type).map(
                (payer) => payer.value.payerId
              );
              const rowPayerIds = sectionRows.map(
                ({ index }) => watchedPayerFees[index]?.payerId
              );
              const sharedRate =
                sectionRows.length > 0
                  ? Number(
                      watchedPayerFees[sectionRows[0].index]?.valueInPercentage
                    )
                  : null;
              const coversAllPayers =
                eligiblePayerIds.length > 0 &&
                rowPayerIds.length === eligiblePayerIds.length &&
                new Set(rowPayerIds).size === rowPayerIds.length &&
                eligiblePayerIds.every((id) => rowPayerIds.includes(id));
              const allSameRate =
                sharedRate !== null &&
                sharedRate > 0 &&
                sectionRows.every(
                  ({ index }) =>
                    Number(watchedPayerFees[index]?.valueInPercentage) ===
                    sharedRate
                );
              const collapsed =
                coversAllPayers && allSameRate && !perPayerView[section.type];
              const setRateForAllRows = (rate: number) =>
                sectionRows.forEach(({ index }) =>
                  setValue(
                    `payerAccountFees.${index}.valueInPercentage`,
                    rate,
                    {
                      shouldDirty: true,
                    }
                  )
                );
              return (
                <div
                  key={section.type}
                  className="block bg-white border-round"
                  data-testid={`payer-fee-section-${section.type}`}
                >
                  <div className="flex justify-content-between mb-3">
                    <span className="text-black font-semibold text-md m-0">
                      {section.title}
                    </span>
                    <Button
                      type="button"
                      label="Add Payer Fee"
                      icon="pi pi-plus"
                      onClick={() => addPayerFee(section.type)}
                      className="p-button-outlined"
                      disabled={sectionDisabled}
                    />
                  </div>
                  {section.help && (
                    <p className="text-gray-600 text-sm mb-3">{section.help}</p>
                  )}
                  {sectionDisabled && (
                    <p
                      className="text-orange-600 text-sm mb-3"
                      data-testid="surcharge-disabled-note"
                    >
                      Enter a Clear fee for GFE greater than 0 to enable the
                      Surcharge fee.
                    </p>
                  )}

                  <div className="flex align-items-end gap-2 mb-3">
                    <div className="field mb-0" style={{ maxWidth: "220px" }}>
                      <Label htmlFor={`allPayers-${section.type}`}>
                        Same % for all payers
                      </Label>
                      <InputText
                        className="w-full"
                        id={`allPayers-${section.type}`}
                        type="number"
                        min={0}
                        step="0.01"
                        placeholder="e.g. 3"
                        value={allPayersPercent[section.type]}
                        onChange={(e) =>
                          setAllPayersPercent((prev) => ({
                            ...prev,
                            [section.type]: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <Button
                      type="button"
                      label="Apply to all payers"
                      icon="pi pi-users"
                      className="p-button-outlined"
                      disabled={
                        sectionDisabled ||
                        !(parseFloat(allPayersPercent[section.type]) > 0)
                      }
                      onClick={() => applyToAllPayers(section.type)}
                    />
                  </div>

                  {sectionNotice[section.type] && (
                    <p
                      className="text-orange-600 text-sm mb-3"
                      data-testid={`payer-fee-notice-${section.type}`}
                    >
                      {sectionNotice[section.type]}
                    </p>
                  )}

                  {sectionRows.length === 0 && (
                    <p className="text-gray-600 mb-3">
                      No payer fees configured.
                    </p>
                  )}

                  {collapsed && (
                    <div
                      className="formgrid grid mb-3 p-3 border-1 border-round surface-border align-items-end"
                      data-testid={`all-payers-summary-${section.type}`}
                    >
                      <div className="field col-5 mb-0">
                        <Label htmlFor={`allPayersRate-${section.type}`}>
                          Payer
                        </Label>
                        <div className="text-black font-semibold py-2">
                          All payers ({sectionRows.length})
                        </div>
                      </div>
                      <div className="field col-5 mb-0">
                        <Label htmlFor={`allPayersRate-${section.type}`}>
                          {section.valueLabel}
                        </Label>
                        <InputText
                          className="w-full"
                          id={`allPayersRate-${section.type}`}
                          type="number"
                          min={0.01}
                          step="0.01"
                          value={String(sharedRate)}
                          onChange={(e) => {
                            const rate = parseFloat(e.target.value);
                            if (rate > 0) setRateForAllRows(rate);
                          }}
                        />
                      </div>
                      <div className="field col-2 mb-0 flex gap-2">
                        <Button
                          type="button"
                          label="Edit per payer"
                          className="p-button-text p-button-sm"
                          onClick={() =>
                            setPerPayerView((prev) => ({
                              ...prev,
                              [section.type]: true,
                            }))
                          }
                        />
                        <Button
                          type="button"
                          icon="pi pi-trash"
                          className="p-button-outlined p-button-danger"
                          aria-label="Remove fee for all payers"
                          onClick={() =>
                            remove(sectionRows.map(({ index }) => index))
                          }
                        />
                      </div>
                    </div>
                  )}

                  {!collapsed && coversAllPayers && allSameRate && (
                    <Button
                      type="button"
                      label="Show as one line for all payers"
                      className="p-button-text p-button-sm mb-2"
                      onClick={() =>
                        setPerPayerView((prev) => ({
                          ...prev,
                          [section.type]: false,
                        }))
                      }
                    />
                  )}

                  {!collapsed &&
                    sectionRows.map(({ field, index }) => (
                      <div
                        key={field.id}
                        className="formgrid grid mb-3 p-3 border-1 border-round surface-border"
                      >
                        <div className="field col-5">
                          <Label htmlFor={`payerAccountFees.${index}.payerId`}>
                            Payer
                          </Label>
                          <Controller
                            name={`payerAccountFees.${index}.payerId`}
                            control={control}
                            rules={{
                              required: "Payer is required",
                              validate: (value) => {
                                if (isPayerAlreadySelected(index, value)) {
                                  return "This payer already has a fee: a payer can have either a Clear Patient Fee or a Surcharge fee";
                                }
                                return true;
                              },
                            }}
                            render={({ field: { onChange, value } }) => {
                              const isDuplicate = isPayerAlreadySelected(
                                index,
                                value
                              );
                              return (
                                <>
                                  <Dropdown
                                    id={`payerAccountFees.${index}.payerId`}
                                    value={value}
                                    onChange={(e) => {
                                      onChange(e.value);
                                      // Trigger validation for all payer fields to update duplicate errors
                                      setTimeout(() => {
                                        watchedPayerFees.forEach(
                                          (_: any, idx: number) => {
                                            trigger(
                                              `payerAccountFees.${idx}.payerId`
                                            );
                                          }
                                        );
                                      }, 0);
                                    }}
                                    options={payerOptionsFor(
                                      section.type,
                                      value
                                    ).map((payer) => ({
                                      label: payer.value.name,
                                      value: payer.value.payerId,
                                    }))}
                                    placeholder="Select Payer"
                                    className={`w-full ${
                                      isDuplicate ? "p-invalid" : ""
                                    }`}
                                  />
                                  {getFormErrorMessage(
                                    `payerAccountFees.${index}.payerId`,
                                    errors
                                  )}
                                </>
                              );
                            }}
                          />
                        </div>

                        <div className="field col-5">
                          <Label
                            htmlFor={`payerAccountFees.${index}.valueInPercentage`}
                          >
                            {section.valueLabel}
                          </Label>
                          <Controller
                            name={`payerAccountFees.${index}.valueInPercentage`}
                            control={control}
                            rules={{
                              required:
                                formValidationConstants.CLEAR_FEE
                                  .CLEAR_PATIENT_FEE,
                              min: {
                                value: 0.01,
                                message: "Value must be greater than 0",
                              },
                            }}
                            render={({
                              field: { onChange, onBlur, value },
                            }) => (
                              <>
                                <InputText
                                  className="w-full"
                                  id={`payerAccountFees.${index}.valueInPercentage`}
                                  autoComplete="off"
                                  type="number"
                                  value={value || ""}
                                  placeholder="Enter fee amount"
                                  min={0}
                                  onBlur={() => {
                                    handleOnBlurField({
                                      onBlur,
                                      field: `payerAccountFees.${index}.valueInPercentage`,
                                      trigger,
                                    });
                                  }}
                                  onChange={onChange}
                                />
                                {getFormErrorMessage(
                                  `payerAccountFees.${index}.valueInPercentage`,
                                  errors
                                )}
                              </>
                            )}
                          />
                        </div>

                        <div className="field col-2 flex align-items-end">
                          <Button
                            type="button"
                            icon="pi pi-trash"
                            className="p-button-danger p-button-outlined w-full"
                            onClick={() => remove(index)}
                            tooltip="Remove payer fee"
                          />
                        </div>
                      </div>
                    ))}
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}
